import { API_BASE } from '../config/api';
import AuthService from './authService';
import { typesForSpineCategory } from '../utils/categoryUtils';

/**
 * Map discovery — ranked "things to do" from our own content store,
 * queried by map viewport + trip date window. See docs/MAP_DISCOVERY_SPEC.md.
 */
class DiscoveryService {
  /**
   * @param {object} opts
   * @param {object} opts.bounds - { north, south, east, west }
   * @param {string[]} [opts.types] - subset of ['sights','events','tours']
   * @param {string} [opts.dateFrom] - YYYY-MM-DD (events filter)
   * @param {string} [opts.dateTo] - YYYY-MM-DD (events filter)
   * @param {number} [opts.zoom] - map zoom; sights are thinned to a few per screen
   * @param {boolean} [opts.padded] - bounds are 2× the view each way (the server
   *   then returns 4× the events and tours, ~60 of them in view)
   * @param {number} [opts.limit] - the map's fixed set: the best N of each group
   *   in the view, sights ranked with no thinning (src/utils/discoverySet.js)
   * @param {string[]} [opts.interests] - sight families to keep (with limit)
   * @returns {Promise<{items: Array, counts: object, bookable: object|null, families: object|null}>}
   */
  static async getMapDiscovery({ bounds, types, dateFrom, dateTo, zoom, padded, limit, interests }) {
    const params = new URLSearchParams({
      north: String(bounds.north),
      south: String(bounds.south),
      east: String(bounds.east),
      west: String(bounds.west)
    });
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (types && types.length) params.set('types', types.join(','));
    if (Number.isFinite(zoom)) params.set('zoom', String(Math.round(zoom)));
    if (padded) params.set('padded', '1');
    if (Number.isFinite(limit)) params.set('limit', String(Math.round(limit)));
    if (interests && interests.length) params.set('interests', interests.join(','));

    const response = await fetch(`${API_BASE}/api/discovery/map?${params.toString()}`, {
      headers: { ...AuthService.getAuthHeaders() }
    });
    if (!response.ok) {
      throw new Error(`Discovery request failed (${response.status})`);
    }
    const data = await response.json();
    // Spine sights carry our category, not Google types: give them the types
    // the pin glyphs and labels read.
    if (Array.isArray(data.items)) {
      data.items = data.items.map(item => (item.source === 'spine' && !(item.types || []).length
        ? { ...item, types: typesForSpineCategory(item.category_id) }
        : item));
    }
    return data;
  }

  /**
   * The stops and length of Discover tours, for drawing them as routes.
   * @param {string[]} ids - tour ids (the server reads at most 30)
   * @returns {Promise<Object<string, {stops: {title, lat, lng}[], stop_count: number, distance_meters: number|null}>>}
   */
  static async getTourRoutes(ids) {
    if (!ids || !ids.length) return {};
    const params = new URLSearchParams({ ids: ids.slice(0, 30).join(',') });
    const response = await fetch(`${API_BASE}/api/discovery/tour-routes?${params.toString()}`, {
      headers: { ...AuthService.getAuthHeaders() }
    });
    if (!response.ok) {
      throw new Error(`Tour routes request failed (${response.status})`);
    }
    const data = await response.json();
    return data.routes || {};
  }

  /**
   * The curated walks whose line meets a view (docs/prompts/WALKING_AREAS.md).
   * @param {{north:number, south:number, east:number, west:number}} bounds
   * @returns {Promise<Object[]>} walks, each with `path` ([[lat, lng], …]) and its card's fields
   */
  static async getWalks(bounds) {
    const params = new URLSearchParams({
      north: bounds.north, south: bounds.south, east: bounds.east, west: bounds.west
    });
    const response = await fetch(`${API_BASE}/api/discovery/walks?${params.toString()}`, {
      headers: { ...AuthService.getAuthHeaders() }
    });
    if (!response.ok) {
      throw new Error(`Walks request failed (${response.status})`);
    }
    const data = await response.json();
    return data.walks || [];
  }

  /** One curated walk by id (its page, a collection's walk), retired ones included; null when there is none. */
  static async getWalk(walkId) {
    const response = await fetch(`${API_BASE}/api/discovery/walks/${encodeURIComponent(walkId)}`, {
      headers: { ...AuthService.getAuthHeaders() }
    });
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(`Walk request failed (${response.status})`);
    }
    const data = await response.json();
    return data.walk || null;
  }
}

export default DiscoveryService;
